"use client";

import { Globe, Package, Truck, Warehouse } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function Underwriting() {
  const {
    ref: imageRef,
    isVisible: imageVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const {
    ref: contentRef,
    isVisible: contentVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const features = [{
    icon: Warehouse,
    title: "Hazardous site assessment",
    description: "Comprehensive evaluation of industrial facilities to identify fire risks and structural vulnerabilities.",
    detail: "ISO-compliant audit protocols"
  }, {
    icon: Truck,
    title: "Risk mitigation strategy",
    description: "Engineered solutions designed to reduce liability and protect high-value industrial assets.",
    detail: "Insurance-grade reporting"
  }, {
    icon: Globe,
    title: "Global compliance standards",
    description: "Navigating complex international fire codes for aerospace and pharmaceutical manufacturing.",
    detail: "Multi-jurisdictional expertise"
  }, {
    icon: Package,
    title: "Underwriting data support",
    description: "Technical documentation and risk modeling to assist insurance partners in policy valuation.",
    detail: "Quantifiable risk metrics"
  }];
  return <section data-section-id="11138" id="underwriting-support" className="dark bg-background border-t border-border py-24 md:py-32">
      <div className="container mx-auto max-w-[1280px] px-6 md:px-12 lg:px-20">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-start">
          {}
          <div ref={imageRef} className={`lg:col-span-6 transition-all duration-700 ease-out ${imageVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}>
            <div className="border border-border overflow-hidden rounded-md">
              <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1705042/img/industrial_risk_assessment.webp" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1705042/img/industrial_risk_assessment.webp-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1705042/img/industrial_risk_assessment.webp-wvc-sizes" alt="Technical fire protection assessment at an industrial facility" loading="lazy" decoding="async" className="w-full h-full object-cover aspect-[4/5]" />
            </div>
          </div>

          {}
          <div ref={contentRef} className="lg:col-span-6">
            <div className={`transition-all duration-700 ease-out ${contentVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}>
              <p className="font-default text-xs font-semibold uppercase tracking-[0.14em] text-muted-foreground">
                Underwriting support
              </p>
              <h2 className="font-default font-semibold text-foreground mt-4 text-[clamp(2rem,3.4vw,3.25rem)] leading-[1.1] tracking-tight">
                Engineered for{" "}
                <em className="font-serif italic font-normal">insurable</em>{" "}
                resilience
              </h2>
              <div className="mt-4 h-px w-16 bg-border" />
              <p className="mt-6 font-default text-muted-foreground text-[clamp(1.0625rem,1.15vw,1.1875rem)] leading-[1.6]">
                Bedrock Safety Engineering provides the technical rigor required by insurance underwriters. We translate complex industrial fire risks into clear, actionable data, ensuring your high-stakes assets are protected and your risk profile is accurately represented.
              </p>
            </div>

            {}
            <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 gap-6">
              {features.map((feature, i) => {
              const Icon = feature.icon;
              return <div key={i} data-index={i} className={`border-t border-border pt-6 transition-all duration-700 ease-out ${contentVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`} style={{
                transitionDelay: contentVisible ? `${i * 80}ms` : "0ms"
              }}>
                    <Icon className="text-primary" strokeWidth={1.5} style={{
                  width: 28,
                  height: 28
                }} />
                    <h3 className="font-default font-semibold text-foreground mt-4 text-[17px] leading-[1.35]">
                      {feature.title}
                    </h3>
                    <p className="font-default text-muted-foreground mt-2 text-[15px] leading-[1.65]">
                      {feature.description}
                    </p>
                    <p className="font-mono text-muted-foreground mt-3 text-[13px] font-medium tracking-[0.02em]">
                      {feature.detail}
                    </p>
                  </div>;
            })}
            </div>

            {}
            <div className="mt-10">
              <Button asChild className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-md h-auto px-7 py-3.5 font-default font-semibold text-sm transition-all duration-200 hover:-translate-y-px">
                <Link to="/ContactPage">Request technical consultation</Link>
              </Button>
            </div>
          </div>
        </div>
      </div>
    </section>;
}